import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ReactComponent as Alert } from '../../../assets/icons/alert-icon.svg';
import DefaultApiLimit from '../../../config/defaultLimit';
import {
  HistoricalFaultInterFace,
  RequestHistoricalFaultInterFace,
} from '../../../interface/historical-faults-interface';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import {
  disableHistoricalFaultFiltersApplied,
  fetchHistoricalFault,
  fetchListHistoricalFaultFilters,
  updateHistoricalFaultFilters,
  setSortData,
} from '../../../redux/slice/HistoricalFaultSlice';
import { setNotificationDetails } from '../../../redux/slice/NotificationSlice';
import CustomTable from '../../../shared-components/custom-table/CustomTable';
import { GetParametersFromCommonFilters } from '../../../shared-functions/CommonFilters';
import ConvertUTCtoDate from '../../../shared-functions/ConvertUTCtoDate';
import ConvertUTCtoHHMM from '../../../shared-functions/ConvertUTCtoHHMM';
import { exportCSV, exportPDF } from '../../../shared-functions/ExportFile';
import { FormatUtcString } from '../../../shared-functions/FormatUtcString';
import stationManagementApi from '../../../utils/api/station-management';

/**
 * Historical Faults
 * @returns {any} The HistoricalFaults component
 */
function HistoricalFaults() {
  const { t } = useTranslation('stationManagement');
  const dispatch = useAppDispatch();
  const [hiddenColumns, setHiddenColumns] = useState<string[]>([]);
  const [exportLoader, setExportLoader] = useState<boolean>(false);
  const {
    HistoricalFault,
    limit,
    page,
    totalPages,
    totalResults,
    loading,
    filterListLoading,
    filters,
    isFilterUpdated,
    sortBy,
    sortField,
  } = useAppSelector((state) => state.HistoricalFault);
  const { selectedSingleStationPerformanceList, selectedChargingPoint } = useAppSelector(
    (state) => state.stationManagement,
  );

  const column = [
    {
      label: t('date'),
      accessorKey: 'createdAt' as keyof HistoricalFaultInterFace,
      sortAllow: true,
      /**
       * Converting timestamp to UTC date
       * @param stationActivity
       * @returns
       */
      customData: (stationActivity: HistoricalFaultInterFace) => (
        <p>{ConvertUTCtoDate(stationActivity.createdAt)}</p>
      ),
    },
    {
      label: t('time'),
      accessorKey: 'time' as keyof HistoricalFaultInterFace,
      sortAllow: true,
      /**
       * Converting timestamp to UTC time
       * @param stationActivity
       * @returns
       */
      customData: (stationActivityTime: HistoricalFaultInterFace) => (
        <p>{FormatUtcString(ConvertUTCtoHHMM(stationActivityTime.createdAt))}</p>
      ),
    },
    {
      label: t('stationName'),
      accessorKey: 'stationName' as keyof HistoricalFaultInterFace,
      sortAllow: true,
    },
    {
      label: t('deviceSn'),
      accessorKey: 'chargingPointSn' as keyof HistoricalFaultInterFace,
      sortAllow: true,
    },
    {
      label: t('deviceName'),
      accessorKey: 'chargingPointName' as keyof HistoricalFaultInterFace,
      sortAllow: true,
    },
    {
      label: t('title'),
      accessorKey: 'title' as keyof HistoricalFaultInterFace,
      sortAllow: true,
    },
    {
      label: t('faults'),
      accessorKey: 'message' as keyof HistoricalFaultInterFace,
      sortAllow: true,
      /**
       * Document view in table
       * @param station
       * @param index
       */
      customData: (historicalFault: HistoricalFaultInterFace) => (
        <div className="document-in-station-table">
          <div className="document-container">
            <span>
              <Alert />
            </span>
            <p className="table-row-each-data">{historicalFault.message}</p>
          </div>
        </div>
      ),
    },
  ];

  /**
   * Handle the fetch the data in api function
   * @param data
   */
  const fetchData = (data: RequestHistoricalFaultInterFace) => {
    if (!loading) {
      dispatch(
        fetchHistoricalFault({
          ...data,
          chargingStation: selectedChargingPoint?.id,
          chargingPointSn: selectedSingleStationPerformanceList?.id,
        }),
      );
    }
  };

  /**
   * Current columns for export
   */
  const currentColumns = [
    { label: t('sNo'), accessorKey: '__serial__' },
    { label: t('date'), accessorKey: 'createdAt' },
    { label: t('time'), accessorKey: 'time' },
    { label: t('stationName'), accessorKey: 'stationName' },
    { label: t('deviceSn'), accessorKey: 'chargingPointSn' },
    { label: t('deviceName'), accessorKey: 'chargingPointName' },
    { label: t('title'), accessorKey: 'title' },
    { label: t('fault'), accessorKey: 'message' },
  ];

  /**
   * Filtering columns for export without hidden columns
   */
  const exportCurrentColumns = currentColumns.filter(
    (col) => col.accessorKey === '__serial__' || !hiddenColumns.includes(col.accessorKey),
  );

  /**
   * Export data function
   */
  const exportDataPdfOrCSV = (type: string) => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    setExportLoader(true);
    const payload = {
      chargingStation: selectedChargingPoint?.id,
      chargingPointSn: selectedSingleStationPerformanceList?.id,
      ...queryParameters,
    };
    if (sortBy) payload.sortBy = sortBy;
    if (sortField) payload.sortField = sortField;
    stationManagementApi
      .fetchHistoricalFaultsListExport(payload)
      .then((response) => {
        // Convert data to CSV format
        const headers = exportCurrentColumns.map((col) => col.label);
        const rows = response.data.map((row: HistoricalFaultInterFace, index: number) =>
          exportCurrentColumns.map((col) => {
            switch (col.accessorKey) {
              case '__serial__':
                return index + 1;
              case 'createdAt':
                return ConvertUTCtoDate(row?.createdAt) || 'N/A';
              case 'time':
                return ConvertUTCtoHHMM(row?.createdAt) || 'N/A';
              default:
                return (row as any)[col?.accessorKey] || 'N/A';
            }
          }),
        );
        if (type === 'csv') {
          exportCSV(headers, rows, 'charging-station-approved.csv');
        } else {
          exportPDF(headers, rows, 'charging-station-approved.pdf');
        }
        setExportLoader(false);
      })
      .catch((error) => {
        setExportLoader(false);
        dispatch(
          setNotificationDetails({
            type: 'error',
            notificationDetails: {
              statusCode: error?.response?.data?.code || 404,
              message: error?.response?.data?.message || t('somethingWentWrong'),
            },
          }),
        );
      });
  };

  /**
   * Function to handle data limit change
   */
  const handleLimitChange = (changeLimit: number) => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    fetchData({
      ...queryParameters,
      page: DefaultApiLimit.page,
      limit: changeLimit,
      sortBy,
      sortField,
    });
  };
  /**
   * Function for sorting table data
   * @param sortData
   */
  const handleSortFunction = (sortData: { sortBy: 'asc' | 'desc'; sortField: string }) => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    dispatch(setSortData(sortData));
    fetchData({
      ...queryParameters,
      sortBy: sortData.sortBy,
      sortField: sortData.sortField,
      page: DefaultApiLimit.page,
      limit,
    });
  };

  /**
   * Function to handle page change
   * @param pageNumber
   */
  const handlePageChange = (pageNumber: number) => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    fetchData({
      ...queryParameters,
      page: pageNumber,
      limit,
      sortBy,
      sortField,
    });
  };
  /**
   * Function to refresh data
   */
  const handleRefresh = () => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    fetchData({ ...queryParameters, page, limit, sortBy, sortField });
  };

  useEffect(() => {
    if (!selectedSingleStationPerformanceList?.id) return;

    const currentId = selectedSingleStationPerformanceList.id;
    const lastId = sessionStorage.getItem('HistoricalFaultFiltersAppliedList'); // string | null

    // If same id as last time -> do nothing
    if (lastId !== currentId || filters.length === 1 || isFilterUpdated) {
      dispatch(
        fetchListHistoricalFaultFilters({
          chargingStation: selectedChargingPoint?.id,
          chargingPointSn: selectedSingleStationPerformanceList?.id,
        }),
      );
    }
    const queryParameters = GetParametersFromCommonFilters(filters);

    if (HistoricalFault.length === 0 || lastId !== currentId) {
      fetchData({
        page,
        limit,
        ...queryParameters,
      });
      dispatch(disableHistoricalFaultFiltersApplied());
    }
    // Save this id so next time we can compare
    sessionStorage.setItem('HistoricalFaultFiltersAppliedList', currentId);
  }, [selectedChargingPoint?.id, filters, selectedSingleStationPerformanceList]);

  return (
    <div style={{ marginTop: '20px' }}>
      <CustomTable
        paginate
        loading={loading}
        limit={limit}
        header={column}
        data={HistoricalFault}
        currentPage={page}
        totalPages={totalPages}
        totalResults={totalResults}
        handlePageChange={handlePageChange}
        handleLimitChange={handleLimitChange}
        tableHeight="60vh"
        version="v2"
        exportDataPdfOrCSV={exportDataPdfOrCSV}
        exportData={HistoricalFault}
        exportLoader={exportLoader}
        topHeader={false}
        filters={filters}
        filtersToDispatch={updateHistoricalFaultFilters}
        filterListLoading={filterListLoading}
        filterListToDispatch={fetchListHistoricalFaultFilters}
        refreshCurrentPages={handleRefresh}
        serialNo
        sortData={{
          sortBy,
          sortField,
        }}
        handleSortChange={handleSortFunction}
        flexDirection
        onHiddenColumnsChange={setHiddenColumns}
      />
    </div>
  );
}

export default HistoricalFaults;
